/**
 * Frontend wrapper for the per-resource ``/export`` CSV endpoints.
 *
 * Every endpoint shares the same shape:
 *   GET /<resource>/export?format=csv[&from=YYYY-MM-DD][&to=YYYY-MM-DD]
 *   → text/csv with Content-Disposition: attachment
 *
 * The auth interceptor on ``client`` injects the bearer token, so we
 * fetch via axios with ``responseType: 'blob'`` and pass the result
 * to the existing ``downloadBlob`` helper. ``window.open`` would
 * skip the auth header and 401.
 */
import client from "./client";
import { downloadBlob } from "./reports";

export type CsvExportResource =
  | "employees"
  | "leave-requests"
  | "time-entries"
  | "invoices"
  | "expenses"
  | "training";

const PATHS: Record<CsvExportResource, string> = {
  employees: "/employees/export",
  "leave-requests": "/leave/requests/export",
  "time-entries": "/time/entries/export",
  invoices: "/invoices/export",
  expenses: "/expenses/export",
  training: "/training/export",
};

const FILENAMES: Record<CsvExportResource, string> = {
  employees: "employees",
  "leave-requests": "leave-requests",
  "time-entries": "time-entries",
  invoices: "invoices",
  expenses: "expenses",
  training: "training",
};

export interface CsvExportOptions {
  /** ISO date (YYYY-MM-DD), inclusive lower bound. */
  from?: string;
  /** ISO date (YYYY-MM-DD), inclusive upper bound. */
  to?: string;
}

/**
 * Fetch the CSV for a resource and trigger a browser download.
 * Throws on HTTP error so the caller can surface a toast.
 */
export async function exportResourceCsv(
  resource: CsvExportResource,
  opts: CsvExportOptions = {},
): Promise<void> {
  const params: Record<string, string> = { format: "csv" };
  if (opts.from) params.from = opts.from;
  if (opts.to) params.to = opts.to;
  const res = await client.get(PATHS[resource], {
    params,
    responseType: "blob",
  });
  const today = new Date().toISOString().slice(0, 10);
  const filename = `${FILENAMES[resource]}-export-${today}.csv`;
  downloadBlob(res.data as Blob, filename);
}
